import type {
  MpClientMessage,
  MpGameMode,
  MpGameStateSnapshot,
  MpRoomSnapshot,
  MpServerMessage,
  MpSubMode,
} from "../../../lib/multiplayer/protocol";
import { mpConnectionTimeoutMs, mpWsUrl } from "../../../lib/multiplayer/protocol";
import { mpSnapshotBuffer } from "./snapshot-buffer";
import { applyMpSnakeTrailDeltas, applyMpSnakeTrailFull, syncMpSnakeTrailColors } from "./mp-snake-trails";

type Listener = (msg: MpServerMessage) => void;

export class MpClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private connectPromise: Promise<void> | null = null;
  playerId: string | null = null;
  room: MpRoomSnapshot | null = null;
  connected = false;
  lastGameState: MpGameStateSnapshot | null = null;
  gameSeed: number | null = null;
  gameSubMode: MpSubMode | null = null;
  gameMode: MpGameMode | null = null;
  gameSpeedrunLevel: number | null = null;
  gameSandboxConfig: import("../../../lib/multiplayer/protocol").MpSandboxConfigWire | null = null;
  speedrunRaceShowCursors = true;
  /** Server wall-clock ms when the current match started (`game_start.startTime`). */
  gameStartTime: number | null = null;
  lastConnectUrl: string | null = null;

  connect(url?: string): Promise<void> {
    const target = url ?? mpWsUrl();
    this.lastConnectUrl = target;

    if (this.connectPromise) return this.connectPromise;

    this.connectPromise = new Promise((resolve, reject) => {
      if (this.ws) this.disconnect();
      const ws = new WebSocket(target);
      this.ws = ws;
      let settled = false;
      const timeoutMs = mpConnectionTimeoutMs(target);

      const clearTimer = () => {
        clearTimeout(timer);
      };

      const fail = (err: Error) => {
        if (settled) return;
        settled = true;
        clearTimer();
        this.connectPromise = null;
        try {
          ws.close();
        } catch {
          /* ignore */
        }
        reject(err);
      };

      const timer = setTimeout(() => {
        fail(new Error("connection_timeout"));
      }, timeoutMs);

      ws.onopen = () => {
        this.connected = true;
      };
      ws.onerror = () => {
        fail(new Error("WebSocket connection failed"));
      };
      ws.onclose = () => {
        this.connected = false;
        this.ws = null;
        this.connectPromise = null;
        if (!settled) fail(new Error("WebSocket closed before ready"));
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(String(ev.data)) as MpServerMessage;
          this.handleServerMessage(msg);
          for (const fn of this.listeners) fn(msg);
          if (!settled && msg.type === "connected") {
            settled = true;
            clearTimer();
            this.connectPromise = null;
            resolve();
          }
        } catch {
          /* ignore */
        }
      };
    });

    return this.connectPromise;
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.connected = false;
    this.connectPromise = null;
    this.playerId = null;
    this.room = null;
    this.lastGameState = null;
    this.gameSeed = null;
    this.gameSubMode = null;
    this.gameMode = null;
    this.gameSpeedrunLevel = null;
    this.gameSandboxConfig = null;
    this.speedrunRaceShowCursors = true;
  }

  onMessage(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  send(msg: MpClientMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  createRoom(name: string, hostAccountId?: string | null, equippedCosmeticIds?: string[]): boolean {
    return this.send({
      type: "create_room",
      name,
      ...(hostAccountId ? { hostAccountId } : {}),
      ...(equippedCosmeticIds?.length ? { equippedCosmeticIds } : {}),
    });
  }

  listFriendRooms(friendAccountIds: string[]): boolean {
    return this.send({ type: "list_friend_rooms", friendAccountIds });
  }

  joinRoom(code: string, name: string, equippedCosmeticIds?: string[]): boolean {
    return this.send({
      type: "join_room",
      code: code.trim().toUpperCase(),
      name,
      ...(equippedCosmeticIds?.length ? { equippedCosmeticIds } : {}),
    });
  }

  leaveRoom(): boolean {
    return this.send({ type: "leave_room" });
  }

  kick(targetId: string): boolean {
    return this.send({ type: "kick", targetId });
  }

  transferHost(targetId: string): boolean {
    return this.send({ type: "transfer_host", targetId });
  }

  selectMode(mode: MpGameMode): boolean {
    return this.send({ type: "select_mode", mode });
  }

  setSpeedrunRaceShowCursors(show: boolean): boolean {
    return this.send({ type: "set_speedrun_race_cursors", show });
  }

  setSnakeBalls(enabled: boolean): boolean {
    return this.send({ type: "set_snake_balls", enabled });
  }

  openModeSetup(): boolean {
    return this.send({ type: "open_mode_setup" });
  }

  selectSpeedrunLevel(level: number): boolean {
    return this.send({ type: "select_speedrun_level", level });
  }

  confirmSandboxConfig(config: import("../../../lib/multiplayer/protocol").MpSandboxConfigWire): boolean {
    return this.send({ type: "confirm_sandbox_config", config });
  }

  cancelModeConfig(): boolean {
    return this.send({ type: "cancel_mode_config" });
  }

  selectSubMode(subMode: MpSubMode): boolean {
    return this.send({ type: "select_submode", subMode });
  }

  cancelSubModeSelect(): boolean {
    return this.send({ type: "cancel_submode_select" });
  }

  openSubModeSelect(): boolean {
    return this.send({ type: "open_submode_select" });
  }

  startGame(): boolean {
    return this.send({ type: "start_game" });
  }

  sendPos(
    x: number,
    y: number,
    seq: number,
    trailEnabled?: boolean,
    trailColor?: string,
  ): boolean {
    return this.send({ type: "pos", x, y, seq, trailEnabled, trailColor });
  }

  returnLobby(): boolean {
    return this.send({ type: "return_lobby" });
  }

  sendSpeedrunFinished(timeMs: number, device: "pc" | "mobile"): boolean {
    return this.send({ type: "speedrun_finished", timeMs, device });
  }

  sendSpeedrunPoints(indices: number[]): boolean {
    return this.send({ type: "speedrun_points", indices });
  }

  dismissSpeedrunResults(): boolean {
    return this.send({ type: "dismiss_speedrun_results" });
  }

  replayMatch(): boolean {
    return this.send({ type: "replay_match" });
  }

  nextSpeedrunLevel(): boolean {
    return this.send({ type: "next_speedrun_level" });
  }

  sendSpeedrunCoopHit(): boolean {
    return this.send({ type: "speedrun_coop_hit" });
  }

  sendHighwayDeath(): boolean {
    return this.send({ type: "highway_death" });
  }

  exitToLobby(): boolean {
    return this.send({ type: "exit_to_lobby" });
  }

  isHost(): boolean {
    return !!(this.playerId && this.room && this.room.hostId === this.playerId);
  }

  private handleServerMessage(msg: MpServerMessage): void {
    switch (msg.type) {
      case "connected":
        this.playerId = msg.playerId;
        break;
      case "room_state":
        this.room = msg.room;
        if (msg.room.speedrunRaceShowCursors != null) {
          this.speedrunRaceShowCursors = msg.room.speedrunRaceShowCursors;
        }
        break;
      case "game_start":
        this.gameSeed = msg.seed;
        this.gameSubMode = msg.subMode;
        this.gameMode = msg.mode;
        this.gameStartTime = msg.startTime;
        this.gameSpeedrunLevel = msg.speedrunLevel ?? null;
        this.gameSandboxConfig = msg.sandboxConfig ?? null;
        if (msg.speedrunRaceShowCursors != null) {
          this.speedrunRaceShowCursors = msg.speedrunRaceShowCursors;
        }
        if (this.room) {
          this.room = { ...this.room, phase: "playing", selectedMode: msg.mode, subMode: msg.subMode };
        }
        break;
      case "game_state":
        this.lastGameState = msg.state;
        mpSnapshotBuffer.push(msg.state);
        applyMpSnakeTrailFull(msg.state.snakeTrailFull);
        applyMpSnakeTrailDeltas(msg.state.snakeTrailDeltas);
        syncMpSnakeTrailColors(
          msg.state.players.map((p) => ({ id: p.id, trailColor: p.trailColor })),
        );
        break;
      case "kicked":
        this.room = null;
        break;
      case "game_over":
        if (this.room) {
          this.room = { ...this.room, phase: "results", resultMessage: msg.message, winnerId: msg.winnerId };
        }
        break;
      case "speedrun_results":
        if (this.room) {
          this.room = {
            ...this.room,
            phase: "speedrun_results",
            speedrunResults: msg.results,
          };
        }
        break;
      case "speedrun_finisher":
        break;
    }
  }
}

export const mpClient = new MpClient();
